import { useCallback, useEffect, useState } from 'react';
import { Box, Grid } from 'components/box';
import { Field, Form } from 'react-final-form';
import { Text } from 'components/typography';
import { Link } from 'components/configurable-routing';
import { closeDrawer, openDrawer } from '../../redux/global/actions';
import { useCreateCustomer } from 'hooks/customer';
import { useDispatch } from 'react-redux';
import Button from 'components/button';
import Divider from './divider';
import LabeledInput from 'components/form/input';
import checkPasswordStrength from '../../util/check-password-strength';
import createDecorator from 'final-form-focus';
import isEmail from 'sane-email-validation';
import config from '../../util/load-config';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ErrorBox } from './error-box';
import Checkbox from 'components/form/checkbox';
import { css } from '@emotion/react';
import SectionTitle from 'components/_shared/elements/section-title';
import { useGlobalConfigMessage, useVodapayActive } from 'hooks/app';
import VodapayAuth from 'components/_shared/widgets/vodapay-authentication';
import SocialAuth from './social-auth';
import { AuthenticationContext, ConfigurableMessageSection } from 'types/types';
import Password from 'components/form/password';
import useRecaptcha from 'hooks/use-recaptcha';

const focusOnErrors = createDecorator();

interface FormFields {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  subscribe: boolean;
}

interface CreateAccountProps {
  showLoginBtn?: boolean;
  loginBtnOnClick?: () => void;
  showCloseBtn?: boolean;
}

const CreateAccount = ({
  showLoginBtn,
  loginBtnOnClick,
  showCloseBtn,
}: CreateAccountProps) => {
  const dispatch = useDispatch();

  const [username, setUsername] = useState('');
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(true);

  const message = useGlobalConfigMessage(
    ConfigurableMessageSection.createAccount
  );

  const [createCustomer, { loading, errors, completed }] = useCreateCustomer();

  const [
    getRecaptcha,
    { loading: recaptchaLoading, hasError: recaptchaError },
  ] = useRecaptcha();

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [completed, dispatch]);

  const close = () => dispatch(closeDrawer());

  const validate = (formFields: FormFields) => {
    const errors: Record<string, string> = {};

    if (!formFields.password) {
      errors.password = 'Enter your password';
    }

    if (!errors.password) {
      const strength = checkPasswordStrength(
        formFields.password,
        formFields.email
      );
      if (!strength.pass) {
        errors.password = strength.message;
      }
    }

    return errors;
  };

  const handleSubmit = useCallback(
    async ({ firstName, lastName, email, password }: FormFields) => {
      const recaptcha = await getRecaptcha('signup');
      if (recaptcha && !recaptchaError && !recaptchaLoading) {
        createCustomer({
          firstName,
          lastName,
          email,
          password,
          recaptcha,
          subscribe: isNewsletterSubscribed,
        });
      }
    },
    [
      createCustomer,
      getRecaptcha,
      isNewsletterSubscribed,
      recaptchaError,
      recaptchaLoading,
    ]
  );

  return (
    <>
      <SectionTitle
        fontSize={[3, 4]}
        title="Create account"
        closeButtonCallback={showCloseBtn ? close : undefined}
      />

      {errors && errors.length > 0 && <ErrorBox errors={errors} />}
      {recaptchaError && <ErrorBox errors={['Recaptcha error']} />}

      <Form<FormFields>
        decorators={[focusOnErrors]}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
          <form
            id="create-account-form"
            autoComplete="on"
            onSubmit={handleSubmit}
            noValidate
          >
            <Grid mt={3}>
              <Field
                name="firstName"
                validate={value => {
                  if (!value) {
                    return 'Enter your first name';
                  }
                  if (value.length < 3) {
                    return 'Please enter a name of at least 3 characters';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <LabeledInput
                      label="First Name"
                      {...input}
                      type="text"
                      invalid={meta.error && meta.touched}
                      error={meta.touched && meta.error}
                      placeholder="First name"
                    />
                  </Box>
                )}
              </Field>

              <Field
                name="lastName"
                validate={value => {
                  if (!value) {
                    return 'Enter your last name';
                  }
                  if (value.length < 3) {
                    return 'Please enter a name of at least 3 characters';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <LabeledInput
                      {...input}
                      label="Last Name"
                      type="text"
                      invalid={meta.error && meta.touched}
                      error={meta.touched && meta.error}
                      placeholder="Last name"
                    />
                  </Box>
                )}
              </Field>
              <Field
                name="email"
                validate={value => {
                  if (!value) {
                    return 'Enter an email';
                  }
                  if (!isEmail(value)) {
                    return 'Enter a valid email';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <LabeledInput
                      {...input}
                      onBlur={() => {
                        setUsername(input.value);
                      }}
                      label="Email Address"
                      autoComplete="username"
                      type="email"
                      error={meta.touched && meta.error}
                      invalid={meta.error && meta.touched}
                      placeholder="Email"
                    />
                  </Box>
                )}
              </Field>

              <Field name="username">
                {({ input }) => (
                  <Box hidden={true}>
                    <LabeledInput
                      {...input}
                      label="username"
                      value={username}
                      autoComplete="username"
                      type="text"
                    />
                  </Box>
                )}
              </Field>

              <Field name="password">
                {({ input, meta }) => (
                  <Box>
                    <Password
                      label="Password"
                      placeholder="Password"
                      {...input}
                      autoComplete="current-password"
                      invalid={meta.error && meta.touched}
                      error={meta.touched && meta.error}
                    />
                  </Box>
                )}
              </Field>

              <Box
                css={theme => css`
                  @media ${theme.mediaQueries.tabletUp} {
                    display: none;
                  }
                `}
              >
                <Checkbox
                  checked={isNewsletterSubscribed}
                  onChange={() =>
                    setIsNewsletterSubscribed(!isNewsletterSubscribed)
                  }
                  label="Sign up for the newsletter!"
                />
              </Box>
              <Box
                css={theme => css`
                  @media ${theme.mediaQueries.mobileOnly} {
                    display: none;
                  }
                `}
              >
                <Checkbox
                  checked={isNewsletterSubscribed}
                  onChange={() =>
                    setIsNewsletterSubscribed(!isNewsletterSubscribed)
                  }
                  label={
                    <span>
                      <b>Want even more deals?</b> Sign up for the newsletter!
                    </span>
                  }
                />
              </Box>

              <Button
                width="100%"
                mb={1}
                mt={2}
                disabled={loading || recaptchaLoading}
                loading={loading || recaptchaLoading}
                type="submit"
                css={theme => css`
                  box-shadow: ${theme.shadows.authButton};
                `}
              >
                Sign up
              </Button>

              <Box>
                <Divider text="Or" fontSize={1} mb={1} />
              </Box>

              <SocialAuth context={AuthenticationContext.SignUp} />
            </Grid>
          </form>
        )}
      </Form>

      <Text textAlign="center" fontSize={2} color="darkGrey" mt={3} mb={4}>
        Do you have an account?{' '}
        <Button
          p="0"
          variant="flat"
          display="inline-block"
          textTransform="none"
          lineHeight={0}
          onClick={() =>
            loginBtnOnClick ? loginBtnOnClick() : dispatch(openDrawer('login'))
          }
          css={theme => css`
            letter-spacing: 0px;
            font-family: ${theme.fonts.body};
          `}
        >
          Log in
        </Button>
      </Text>

      {!!message && (
        <Text
          textAlign="center"
          fontSize="2"
          color="pink"
          fontWeight={600}
          lineHeight={1.3}
          mb={3}
        >
          {message.message}
        </Text>
      )}

      <Box>
        <Divider mb={1} />
      </Box>

      {showLoginBtn && (
        <>
          <Text
            mt={3}
            mb={2}
            fontSize={2}
            color="darkGrey"
            textAlign="center"
            lineHeight="2"
          >
            By signing up you agree to our{' '}
            <Link
              target="_blank"
              href={config.helpCentreLinks.termsAndConditions}
            >
              terms and conditions
            </Link>
            .
          </Text>
          <Box>
            <Text
              textAlign="center"
              fontSize={2}
              color="darkGrey"
              lineHeight={1.5}
            >
              This site is protected by reCAPTCHA and the Google{' '}
              <Link href="https://policies.google.com/privacy">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://policies.google.com/terms">
                Terms of Service
              </Link>{' '}
              apply.
            </Text>
          </Box>
        </>
      )}
    </>
  );
};

const Wrapper = (props: CreateAccountProps) => {
  const vodapayIsActive = useVodapayActive();
  if (vodapayIsActive) {
    return <VodapayAuth />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.recaptcha.key}>
      <CreateAccount {...props} />
    </GoogleReCaptchaProvider>
  );
};

export default Wrapper;
